import { Flex, Text, variables } from '@bitrise/bitkit';
import {
  SeriesPointXY,
  useGetNearestPoint,
  Viz,
  VizHTML,
  VizHTMLArtboard,
  VizSVG,
  VizSVGAxisY,
  VizSVGGridLinesAxisY,
  VizSVGLine,
} from '@bitrise/bitviz';
import { BuildAttributes, BuildMetricsTimeSeries } from '@trace/types';
import React, { FunctionComponent, PointerEvent, useState } from 'react';
import max from 'lodash.max';
import min from 'lodash.min';
import { DurationUnit } from 'luxon';

import MessageFailing from '../../ViewVisualisations/MessageFailing';
import MessageFetching from '../../ViewVisualisations/MessageFetching';
import BuildSeriesKey from './BuildSeriesKey';
import getFormattedValue from '../../../utils/metric/getFormattedValue';
import getCombinedData from './getCombinedData';
import BuildTooltip from '../BuildTooltip';
import BuildTimeOverTimeTooltipContent from './BuildTimeOverTimeTooltipContent';

export interface Props {
  branch: string;
  dataBranchOverTime?: BuildMetricsTimeSeries[];
  dataOverTime: BuildMetricsTimeSeries[];
  isError: boolean;
  isFetching: boolean;
  refetch: () => void;
  step: DurationUnit;
  stepSize: number;
  ticks: number[];
}

export interface GetAvgMaxSeriesResult {
  seriesAvgMax: [SeriesPointXY[], SeriesPointXY[]];
  yMin: number;
  yMax: number;
}

const SERIES_GAP = 3;

const getAvgSeries = (data: BuildMetricsTimeSeries, xOffset = 0) => {
  const series: SeriesPointXY[] = [];

  for (let i = 0; i < data.totalBuildsSuccess.length; i += 1) {
    const x = i + xOffset;
    const y = data.totalTimeSuccess[i] / data.totalBuildsSuccess[i] || data.maxTimeSuccess[i];
    series[i] = { x, y };
  }

  return series;
};

const getAvgMaxSeries = (
  dataOverTime?: BuildMetricsTimeSeries,
): [SeriesPointXY[], SeriesPointXY[]] => {
  const seriesAvgMax: GetAvgMaxSeriesResult['seriesAvgMax'] = [[], []];

  if (!dataOverTime) {
    return seriesAvgMax;
  }

  seriesAvgMax[0] = getAvgSeries(dataOverTime);
  seriesAvgMax[1] = dataOverTime.maxTimeSuccess.map((y, x) => ({ x, y }));

  return seriesAvgMax;
};

const getBranchOtherSeries = (
  branch: string,
  dataOverTime?: BuildMetricsTimeSeries,
  dataBranchOverTime?: (BuildMetricsTimeSeries & BuildAttributes)[],
): [SeriesPointXY[], SeriesPointXY[]] => {
  if (!dataOverTime || !dataBranchOverTime || !dataBranchOverTime.length) {
    return [[], []];
  }

  const dataBranchTarget = dataBranchOverTime.find((data) => data.branch === branch);
  const dataBranchesOther = dataBranchOverTime.filter((data) => data.branch !== branch);
  const dataBranchOther = getCombinedData(dataBranchesOther);
  const seriesBranchOther: [SeriesPointXY[], SeriesPointXY[]] = [[], []];

  const offset = dataOverTime.totalBuildsSuccess.length - 1 + SERIES_GAP;

  if (dataBranchTarget) seriesBranchOther[0] = getAvgSeries(dataBranchTarget, offset);
  if (dataBranchOther) seriesBranchOther[1] = getAvgSeries(dataBranchOther, offset);

  return seriesBranchOther;
};

const getMaxSeries = (
  [a, b]: [SeriesPointXY[], SeriesPointXY[]],
  [c, d]: [SeriesPointXY[], SeriesPointXY[]],
): SeriesPointXY[] => [
  ...b.map<SeriesPointXY>(({ x, y }, i) => ({ x, y: max([y, a[i]?.y]) || null })),
  ...d.map<SeriesPointXY>(({ x, y }, i) => ({ x, y: max([y, c[i]?.y]) || null })),
];

const getYBounds = (
  [a, b]: [SeriesPointXY[], SeriesPointXY[]],
  [c, d]: [SeriesPointXY[], SeriesPointXY[]],
): [number, number] => {
  const ys = [...a, ...b, ...c, ...d].map(({ y }) => y);
  const yMin = min(ys);
  const yMax = max(ys);

  return [yMin || 0, yMax || (yMin || 0) + 1];
};

const BuildTimeOverTime: FunctionComponent<Props> = ({
  branch,
  dataBranchOverTime,
  dataOverTime,
  isError,
  isFetching,
  refetch,
  step,
  stepSize,
  ticks,
}) => {
  const [focusPoint, setFocusPoint] = useState<null | SeriesPointXY>(null);
  const seriesAvgMax = getAvgMaxSeries(dataOverTime[0]);
  const seriesBranchOther = getBranchOtherSeries(branch, dataOverTime[0], dataBranchOverTime);
  const maxSeries = getMaxSeries(seriesAvgMax, seriesBranchOther);
  const [yMin, yMax] = getYBounds(seriesAvgMax, seriesBranchOther);
  const getNearestPoint = useGetNearestPoint(maxSeries, { axis: 'x' });
  const hasBranchComparisonData = !!dataBranchOverTime;

  const ticksDoubled = hasBranchComparisonData
    ? Array.from({ length: ticks.length * 2 + SERIES_GAP }).map((_, i) => i)
    : Array.from({ length: ticks.length }).map((_, i) => i);

  const focusPointNormalized =
    focusPoint &&
    focusPoint.x !== null &&
    (focusPoint.x >= ticks.length - 1 + SERIES_GAP
      ? focusPoint.x - ticks.length + 1 - SERIES_GAP
      : focusPoint.x);

  const onPointerMove = (_: PointerEvent, point?: SeriesPointXY) => {
    if (point && point.x !== null) {
      const nearestPoint = getNearestPoint(point);

      if (nearestPoint && nearestPoint.x !== null) {
        setFocusPoint(nearestPoint);
        return;
      }
    }

    setFocusPoint(null);
  };

  return (
    <Flex container>
      <Flex direction="horizontal" margin="x4">
        <Flex alignChildrenHorizontal="middle" direction="horizontal" gap="x5" grow initial="none">
          <Flex>
            <BuildSeriesKey color="yellow-4">Average</BuildSeriesKey>
          </Flex>

          <Flex>
            <Text config="7">vs</Text>
          </Flex>

          <Flex>
            <BuildSeriesKey color="grape-2">Max</BuildSeriesKey>
          </Flex>
        </Flex>

        {hasBranchComparisonData && (
          <Flex
            alignChildrenHorizontal="middle"
            direction="horizontal"
            gap="x5"
            grow
            initial="none"
          >
            <Flex>
              <BuildSeriesKey color="aqua-3">Default branch</BuildSeriesKey>
            </Flex>

            <Flex>
              <Text config="7">vs</Text>
            </Flex>

            <Flex>
              <BuildSeriesKey color="grape-5">Others</BuildSeriesKey>
            </Flex>
          </Flex>
        )}
      </Flex>

      <Viz
        height={100}
        paddingLeft={34}
        onPointerMove={onPointerMove}
        onPointerLeave={onPointerMove}
        xDomain={ticksDoubled}
        xDomainScale="band"
        xDomainPaddingOuter={0.5}
        yDomain={[yMin, yMax]}
      >
        <VizSVG>
          <VizSVGAxisY
            fill={variables.colorGray6}
            flip
            format={(y) => getFormattedValue('seconds', y)}
            tickLineLength={0.001}
            tickCount={3}
            ticksOnly
          />

          {!isError && !isFetching && (
            <VizSVGGridLinesAxisY
              animate
              stroke={variables.colorGray5}
              strokeDasharray="2 6"
              style={{ pointerEvents: 'none' }}
              tickCount={3}
            />
          )}

          <VizSVGLine
            animate
            originY={yMin}
            series={seriesBranchOther[0]}
            stroke={variables.colorAqua3}
            strokeWidth="2"
          />

          <VizSVGLine
            animate
            originY={yMin}
            series={seriesBranchOther[1]}
            stroke={variables.colorGrape5}
            strokeWidth="2"
          />

          <VizSVGLine
            animate
            originY={yMin}
            series={seriesAvgMax[0]}
            stroke={variables.colorYellow4}
            strokeWidth="2"
          />

          <VizSVGLine
            animate
            originY={yMin}
            series={seriesAvgMax[1]}
            stroke={variables.colorGrape2}
            strokeWidth="2"
          />
        </VizSVG>

        <VizHTML>
          <VizHTMLArtboard>
            <MessageFetching fetching={isFetching} />
            <MessageFailing failing={isError} onFetch={refetch} />
          </VizHTMLArtboard>
        </VizHTML>

        <BuildTooltip point={focusPoint}>
          {focusPointNormalized !== null && focusPointNormalized !== false && (
            <BuildTimeOverTimeTooltipContent
              seriesAvgMax={seriesAvgMax}
              seriesBranchOther={seriesBranchOther}
              step={step}
              stepSize={stepSize}
              timestamp={ticks[focusPointNormalized]}
              x={focusPointNormalized}
            />
          )}
        </BuildTooltip>
      </Viz>
    </Flex>
  );
};

export default BuildTimeOverTime;
